// The signed-in user's own password, and forgot-password by email
// (go-api internal/handlers/password_reset_handlers.go).
//
// Plain fetch, not the axios client: a wrong current password answers 401,
// which the client would take for an expired session and sign the user out.

import { getAuthToken } from './client';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function call(path: string, init: RequestInit & { auth?: boolean } = {}): Promise<string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = init.auth ? getAuthToken() : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch {
    throw new Error('Network error. Please try again.');
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
  return body?.message ?? '';
}

export const accountApi = {
  /** Change the signed-in user's password. The server signs out every session. */
  changePassword: (currentPassword: string, newPassword: string) =>
    call('/auth/password', {
      method: 'PUT',
      auth: true,
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  /** True when the server can mail reset links (SMTP is set up). */
  passwordResetEnabled: async (): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/auth/password-reset/status`);
      return res.ok && (await res.json())?.enabled === true;
    } catch {
      return false;
    }
  },

  /** Ask for a reset link. The answer never says whether the account exists. */
  forgotPassword: (email: string, tenantSlug: string) =>
    call('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email, tenant_slug: tenantSlug }) }),

  /** Set a new password with the token from the emailed link. */
  resetPassword: (token: string, newPassword: string) =>
    call('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, new_password: newPassword }) }),
};

/** The console's password rule, as the server checks it. */
export function passwordProblem(password: string, confirm: string): string {
  if (password.length < 8) return 'Use at least 8 characters.';
  if (new TextEncoder().encode(password).length > 72) return 'Use at most 72 bytes.';
  if (password !== confirm) return "The passwords don't match.";
  return '';
}
